/**
 * Sesotho: Signing in and getting started
 *
 * What this file covers: Sign in, sign up, password reset, two-step sign-in, email verification, the data
 * processing agreement and the first-run onboarding.
 *
 * Tone: Warm, calm and reassuring, in very plain words: this is a business owner's first impression. Security
 * messages stay exact (a wrong code is wrong, an expired link is expired). The agreement text is legal:
 * translate it faithfully, never shorten or soften it.
 *
 * Sesotho: Everyday Sesotho as used in South African business. Keys not translated yet fall back to English, so
 * a missing key is never an error.
 *
 * Rules for every translation: keep each key exactly as it is and translate only the text after it. Keep
 * {{placeholders}} such as {{count}} or {{name}} unchanged, and keep product names (RepletoryReach, WhatsApp,
 * Meta, Google, Outlook) as they are. Never use the em dash.
 */

import type { Catalog } from '../types';

const signIn: Catalog = {
  'auth.common.password': 'Phasewete',
  'auth.common.codeMismatch': 'Khoutu eo ha e tsamaellane.',
  'auth.common.backToSignIn': 'Khutlela ho kena',
  'auth.login.newHere': 'U mocha mona?',
  'auth.login.createAccount': 'Theha ak\'haonte',
  'auth.login.forgotPassword': 'U lebetse phasewete?',
  'auth.login.failed': 'Ho kena ho hlolehile.',
  'auth.login.twofa.form.code': 'Khoutu ea netefatso',
  'auth.login.twofa.verify': 'Netefatsa',
  'auth.signup.title': 'Theha ak\'haonte ea hau',
  'auth.signup.haveAccount': 'U se u na le ak\'haonte?',
  'auth.signup.form.orgName': 'Lebitso la mokhatlo',
  'auth.signup.form.workEmail': 'Imeile ea mosebetsi',
  'auth.signup.submit': 'Theha ak\'haonte',
  'auth.signup.failed': 'Ho ingolisa ho hlolehile.',
  'auth.forgot.title': 'Beha phasewete ea hau bocha',
  'auth.forgot.submit': 'Romela sehokelo sa ho beha bocha',
  'auth.forgot.sent.title': 'Sheba lebokoso la hau le kenang',
  'auth.reset.title': 'Khetha phasewete e ncha',
  'auth.reset.form.newPassword': 'Phasewete e ncha',
  'auth.reset.form.confirmPassword': 'Netefatsa phasewete',
  'auth.reset.submit': 'Beha phasewete bocha',
  'auth.reset.errors.tooShort': 'Phasewete e tlameha ho ba le litlhaku tse 8 bonyane.',
  'auth.reset.errors.mismatch': 'Liphasewete ha li tsamaellane.',
  'auth.reset.failed': 'Ha ea khona ho beha phasewete bocha.',
  'auth.reset.toast.doneTitle': 'Phasewete e behiloe bocha',
  'auth.verify.title': 'Netefatsa imeile ea hau',
  'auth.verify.form.code': 'Khoutu ea linomoro tse 6',
  'auth.verify.submit': 'Netefatsa imeile',
  'auth.verify.resend': 'Romela khoutu hape',
  'auth.verify.backToDashboard': 'Khutlela ho deshboard',
  'auth.verify.toast.done': 'Imeile ea hau e netefalitsoe.',
  'auth.verify.toast.resentTitle': 'Khoutu e rometsoe',
  'auth.verify.toast.resendFailed': 'Ha ea khona ho romela hape.',
  'auth.verify.toast.resendFailedTitle': 'Ho romela hape ho hlolehile',
  'auth.mfa.title': 'Ho kena ka mekhahlelo e \'meli',
  'auth.mfa.goToSignIn': 'Ea ho kena',
  'onboarding.title': 'Rea u amohela ho RepletoryReach',
  'onboarding.skipSetup': 'Tlola tlhophiso',
  'onboarding.steps.verify.label': 'Netefatsa imeile',
  'onboarding.steps.verify.title': 'Netefatsa imeile ea hau',
  'onboarding.steps.verify.form.code': 'Khoutu ea netefatso',
  'onboarding.steps.verify.resend': 'Romela khoutu hape',
  'onboarding.steps.verify.skip': 'Tlola hajoale',
  'onboarding.steps.verify.confirm': 'Netefatsa',
  'onboarding.steps.region.label': 'Sebaka',
  'onboarding.steps.region.title': 'U sebetsa hokae?',
  'onboarding.steps.region.form.country': 'Naha',
  'onboarding.steps.region.country.za': 'Afrika Boroa (ZAR)',
  'onboarding.steps.region.country.ng': 'Nigeria (NGN)',
  'onboarding.steps.region.country.ke': 'Kenya (KES)',
  'onboarding.steps.name.label': 'Lebitso la hau',
  'onboarding.steps.name.title': 'Re u bitse mang?',
  'onboarding.steps.name.form.displayName': 'Lebitso le bonoang',
  'onboarding.steps.number.label': 'Nomoro ea WhatsApp',
  'onboarding.steps.number.title': 'Hokela nomoro ea WhatsApp',
  'onboarding.steps.number.openNumbers': 'Bula Linomoro',
  'onboarding.steps.done.label': 'E phethiloe',
  'onboarding.steps.done.title': 'U se u loketse',
  'onboarding.steps.done.goToDashboard': 'Ea ho deshboard',
  'onboarding.toast.emailVerified': 'Imeile e netefalitsoe.',
  'onboarding.toast.emailVerifiedTitle': 'Ho kenngoa',
  'onboarding.toast.codeMismatch': 'Khoutu ha e tsamaellane.',
  'onboarding.toast.codeMismatchTitle': 'Netefatso',
  'onboarding.toast.saveFailed': 'Ha ea khona ho boloka.',
  'onboarding.toast.profileFailedTitle': 'Boitsebiso',
  'onboarding.stepOf': 'Mohato oa {{step}} ho tse {{total}}',
  'onboarding.stepLabel': 'Mohato',
  'auth.social.or': 'kapa',
  'auth.social.continueWith': 'Tswela pele ka {{provider}}',
  'auth.social.provider': 'mofani wa hao',
  'auth.social.hint': 'Re fumana feela lebitso la hao le aterese ya imeile, mme feela ka mora hore o dumele.',
  'auth.callback.signingIn': 'Re a o kenya…',
  'auth.callback.failedTitle': 'Ho kena hoo ha hoa phethela',
  'auth.callback.failedLead': 'Ha ho letho le fetohileng akhaonteng ya hao. Leka hape, kapa o kene ka aterese ya hao ya imeile le phasewete.',
  'auth.callback.backToSignIn': 'Kgutlela ho kena'
};

export default signIn;
